import { observer } from "mobx-react-lite";
import { Layers, Flame, Zap, Truck, ArrowUpRight, Info } from "lucide-react";
import { useTran } from "@/hooks";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import { cn } from "@/lib/utils";
import { SelectField } from "@/components/common";
import SummaryCard from "../../SummaryCard";
import { EMISSIONS_BREAKDOWN_SCOPE_BY_CARD } from "../../../domains/emissions-breakdown";
import { formatPercentage, formatValue } from "../../../domains/formatting";
import { SCOPE_KEYS } from "../../../domains/scope";
import { dashboardEChartsStore } from "../../../stores/e-charts.store";
import { dashboardEmissionsBreakdownStore } from "../../../stores/emissions-breakdown.store";
import { dashboardEChartsTotalsStore } from "../../../stores/e-charts-totals.store";
import { dashboardFiltersStore } from "../../../stores/filters.store";
import { sortReportingYearsDescending } from "../../../utils/reporting-years";

const NONE_VALUE = "__none__";

const KPI_CARDS = [
  {
    key: "total",
    label: "Total Emissions",
    icon: Layers,
    tone: "success",
    info: "Sum of Scope 1 + Scope 2 + Scope 3 for the selected year, emission from all entities.",
  },
  {
    key: "scope1",
    label: "Scope 1",
    icon: Flame,
    tone: "info",
    info: "Direct emissions from owned or controlled sources (e.g. fuel combustion) for the selected year, emission from all entities.",
  },
  {
    key: "scope2",
    label: "Scope 2",
    icon: Zap,
    tone: "warning",
    info: "Indirect emissions from purchased electricity, steam, heat, or cooling for the selected year, emission from all entities.",
  },
  {
    key: "scope3",
    label: "Scope 3",
    icon: Truck,
    tone: "violet",
    info: "All other indirect emissions in the value chain (e.g. ingredients, packaging, logistics) for the selected year, emission from all entities.",
  },
];

const InfoDot = ({ tip }) => (
  <span
    title={tip}
    className="inline-flex cursor-pointer text-muted-foreground hover:text-foreground"
  >
    <Info className="h-3 w-3" />
  </span>
);

const TitleWithInfo = ({ label, tip }) => (
  <span className="inline-flex items-center gap-1">
    {label}
    <InfoDot tip={tip} />
  </span>
);

const DeltaChip = ({ delta, baseYear, formulaTip }) => {
  if (!Number.isFinite(delta)) return null;
  const isFlat = delta === 0;
  const tone = isFlat
    ? "text-muted-foreground"
    : delta < 0
      ? "text-primary"
      : "text-emerald-600";
  return (
    <span className="inline-flex items-center gap-1 text-[11px]">
      <span className={cn("inline-flex items-center font-medium", tone)}>
        {delta > 0 ? "+" : ""}
        {formatPercentage(delta)}
      </span>
      <span className="text-muted-foreground">vs {baseYear}</span>
      <InfoDot tip={formulaTip} />
    </span>
  );
};

const ValueSkeleton = () => (
  <span className="inline-block h-7 w-28 animate-pulse rounded bg-muted/40" />
);
const SubtitleSkeleton = () => (
  <span className="inline-block h-3 w-20 animate-pulse rounded bg-muted/40" />
);
const BREAKDOWN_ACTION_CLASSNAME =
  "inline-flex items-center gap-1 text-[11px] font-medium text-primary transition-colors group-hover:text-primary/80";
const BREAKDOWN_ACTION_DISABLED_CLASSNAME =
  "inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground";

const EChartsKpiStrip = observer(() => {
  const { t } = useTran();
  const store = dashboardEChartsTotalsStore;
  const chartStore = dashboardEChartsStore;
  const { years: availableYears = [], reportingYears = [] } =
    dashboardFiltersStore.available;
  const selectedYear = chartStore.selectedYear;
  const baseYear = store.baselineYear;
  const sameAsBase = selectedYear === baseYear;
  const isFirstLoad = !store.hasLoadedOnce;
  const formulaTip = t(
    `% change vs base year. Formula: (selected year - ${baseYear}) / ${baseYear} * 100`,
  );

  const selectedYearStr =
    selectedYear == null ? NONE_VALUE : String(selectedYear);
  const yearOptions = [
    { value: NONE_VALUE, label: t("None") },
    ...(reportingYears.length > 0
      ? sortReportingYearsDescending(reportingYears).map((reportingYear) => ({
          value: String(reportingYear.startYear),
          label: formatReportingYearPeriod(reportingYear),
        }))
      : [...availableYears]
          .sort((a, b) => b - a)
          .map((year) => ({ value: String(year), label: String(year) }))),
  ];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("Selected year")}
        </span>
        <SelectField
          key={`kpi-selected-year-${selectedYearStr}`}
          options={yearOptions}
          value={selectedYearStr}
          onValueChange={(value) =>
            chartStore.setSelectedYear(value === NONE_VALUE ? "" : value)
          }
          placeholder={t("None")}
          sortOptions={false}
          selectClassName="h-8 w-75 text-xs"
        />
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
        {KPI_CARDS.map((card) => {
          const value = store.cellFor(selectedYear, card.key);
          const hasValue = Number.isFinite(value);
          const isBreakdownDisabled = store.loading || !hasValue;
          const baseValue = store.cellFor(baseYear, card.key);
          const delta =
            !sameAsBase &&
            hasValue &&
            Number.isFinite(baseValue) &&
            baseValue !== 0
              ? ((value - baseValue) / baseValue) * 100
              : null;

          return (
            <button
              key={card.key}
              type="button"
              onClick={
                isBreakdownDisabled
                  ? undefined
                  : () =>
                      dashboardEmissionsBreakdownStore.openForScope(
                        EMISSIONS_BREAKDOWN_SCOPE_BY_CARD[card.key],
                      )
              }
              disabled={isBreakdownDisabled}
              className={cn(
                "group h-full w-full text-left",
                isBreakdownDisabled && "cursor-not-allowed opacity-70",
              )}
            >
              <SummaryCard
                title={
                  <TitleWithInfo label={t(card.label)} tip={t(card.info)} />
                }
                headerAccessory={
                  isFirstLoad ? (
                    <SubtitleSkeleton />
                  ) : delta != null ? (
                    <DeltaChip
                      delta={delta}
                      baseYear={baseYear}
                      formulaTip={formulaTip}
                    />
                  ) : null
                }
                value={
                  isFirstLoad ? (
                    <ValueSkeleton />
                  ) : hasValue ? (
                    <>
                      {formatValue(value)}{" "}
                      <span className="text-xs font-normal text-muted-foreground">
                        tCO2e
                      </span>
                    </>
                  ) : (
                    "—"
                  )
                }
                icon={card.icon}
                tone={card.tone}
                actionAlignment="start"
                action={
                  <span
                    className={
                      isBreakdownDisabled
                        ? BREAKDOWN_ACTION_DISABLED_CLASSNAME
                        : BREAKDOWN_ACTION_CLASSNAME
                    }
                  >
                    {hasValue
                      ? t("Click to explore breakdown")
                      : t("No breakdown available")}
                    {hasValue ? <ArrowUpRight className="h-3.5 w-3.5" /> : null}
                  </span>
                }
              />
            </button>
          );
        })}
      </div>
    </div>
  );
});

export default EChartsKpiStrip;
export { SCOPE_KEYS };
